'use client';

import { useEffect, useMemo, type ReactElement } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, type SubmitHandler } from 'react-hook-form';
import { useFormWithBlurValidation } from '@/hooks/use-form-with-blur-validation';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { CustomInput } from '@/components/ui/custom-input';
import { CustomPhoneInput } from '@/components/ui/custom-phone-input';
import { CustomTextarea } from '@/components/ui/custom-textarea';
import { DismissCloseButton } from '@/components/ui/dismiss-close-button';
import { FieldCharacterCount } from '@/components/ui/field-character-count';
import { RequiredFieldsLegend } from '@/components/required-fields-legend';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetTitle } from '@/components/ui/sheet';
import { useMediaQuery } from '@/hooks/use-media-query';
import {
    createProductGiftSchema,
    PRODUCT_GIFT_MESSAGE_MAX_LENGTH,
    type ProductGiftDetails,
    type ProductGiftFormValues,
} from '@/lib/product-gift-schemas';
import { normalizeOptionalPhone } from '@/lib/phone-validation';
import { getProductGiftContent, includesGiftRecipientPhone } from '@/lib/product-utils';
import { cn } from '@/lib/utils';
import type { SCAPIShopperProductsSchemas } from 'src/types';

interface ProductGiftDrawerProps {
    product: Pick<SCAPIShopperProductsSchemas['Product'], 'c_productType'>;
    open: boolean;
    initialValues: ProductGiftDetails | null;
    onOpenChange: (open: boolean) => void;
    onSave: (details: ProductGiftDetails) => void;
    onCancel: () => void;
    onCloseAutoFocus?: (event: Event) => void;
}

export default function ProductGiftDrawer({
    product,
    open,
    initialValues,
    onOpenChange,
    onSave,
    onCancel,
    onCloseAutoFocus,
}: ProductGiftDrawerProps): ReactElement {
    const { t } = useTranslation('product');
    const isDesktop = useMediaQuery('(min-width: 1024px)');
    const includesPhone = includesGiftRecipientPhone(product);
    const content = getProductGiftContent(product, t);

    const schema = useMemo(
        () => createProductGiftSchema(t, { requiresPhone: false, validateOptionalPhone: includesPhone }),
        [includesPhone, t]
    );

    const defaultValues = useMemo<ProductGiftFormValues>(
        () => ({
            recipientName: initialValues?.recipientName ?? '',
            recipientEmail: initialValues?.recipientEmail ?? '',
            recipientPhone: initialValues?.recipientPhone ?? '',
            message: initialValues?.message ?? '',
        }),
        [initialValues]
    );

    const {
        register,
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useFormWithBlurValidation<ProductGiftFormValues>({
        resolver: zodResolver(schema),
        mode: 'onBlur',
        defaultValues,
    });

    useEffect(() => {
        if (open) {
            reset(defaultValues);
        }
    }, [defaultValues, open, reset]);

    const messageLength = (watch('message') ?? '').length;

    const handleSave: SubmitHandler<ProductGiftFormValues> = (data) => {
        const normalizedPhone = normalizeOptionalPhone(data.recipientPhone);
        const trimmedMessage = data.message?.trim();

        onSave({
            recipientName: data.recipientName.trim(),
            recipientEmail: data.recipientEmail.trim(),
            ...(normalizedPhone ? { recipientPhone: normalizedPhone } : {}),
            ...(trimmedMessage ? { message: trimmedMessage } : {}),
        });
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                hideClose
                side={isDesktop ? 'right' : 'bottom'}
                aria-describedby="product-gift-drawer-description"
                data-testid="product-gift-drawer"
                onCloseAutoFocus={onCloseAutoFocus}
                autoFocus
                className={cn(
                    'gap-0 border-neutral-80 bg-neutral-white p-0 focus:outline-none',
                    isDesktop && 'h-full w-full max-w-[480px] border-l sm:max-w-[480px]',
                    !isDesktop && 'max-h-[min(90dvh,720px)] rounded-t-lg border-t'
                )}>
                <form
                    className="flex max-h-[inherit] flex-1 flex-col overflow-hidden"
                    onSubmit={(event) => void handleSubmit(handleSave)(event)}
                    noValidate>
                    <div className="flex shrink-0 items-start justify-between gap-6 px-6 pt-6">
                        <SheetTitle className="title-m pr-2 text-foreground">{content.drawerTitle}</SheetTitle>
                        <DismissCloseButton aria-label={t('gift.closeDrawer')} onClick={onCancel} />
                    </div>

                    <SheetDescription id="product-gift-drawer-description" className="body-copy-m px-6 pt-4">
                        {t('gift.drawerDescription')}
                    </SheetDescription>

                    <div className="flex min-h-0 flex-1 flex-col gap-y-4 overflow-y-auto px-6 pb-6 pt-6">
                        <RequiredFieldsLegend />
                        <CustomInput
                            id="product-gift-recipient-name"
                            label={t('gift.recipientNameLabel')}
                            errorMessage={errors.recipientName?.message}
                            errorId="product-gift-recipient-name-error"
                            hintId="product-gift-recipient-name-hint"
                            hintText={t('gift.recipientNamePlaceholder')}
                            autoComplete="name"
                            inputClassName="w-full border-border"
                            required
                            {...register('recipientName')}
                        />

                        <CustomInput
                            id="product-gift-recipient-email"
                            label={t('gift.recipientEmailLabel')}
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            errorMessage={errors.recipientEmail?.message}
                            errorId="product-gift-recipient-email-error"
                            hintId="product-gift-recipient-email-hint"
                            hintText={t('gift.recipientEmailPlaceholder')}
                            inputClassName="w-full border-border"
                            required
                            {...register('recipientEmail')}
                        />

                        {includesPhone ? (
                            <Controller
                                name="recipientPhone"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <CustomPhoneInput
                                        id="product-gift-recipient-phone"
                                        label={t('gift.recipientPhoneLabel')}
                                        errorMessage={fieldState.error?.message}
                                        errorId="product-gift-recipient-phone-error"
                                        hintId="product-gift-recipient-phone-hint"
                                        hintText={t('gift.recipientPhonePlaceholder')}
                                        name={field.name}
                                        ref={field.ref}
                                        value={field.value}
                                        onBlur={field.onBlur}
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                        ) : null}

                        <div className="flex flex-col gap-2">
                            <CustomTextarea
                                id="product-gift-message"
                                label={t('gift.messageLabel')}
                                rows={4}
                                errorMessage={errors.message?.message}
                                errorId="product-gift-message-error"
                                hintId="product-gift-message-hint"
                                hintText={t('gift.messagePlaceholder')}
                                labelClassName="top-8"
                                textareaClassName="min-h-[100px]"
                                {...register('message')}
                            />
                            <FieldCharacterCount
                                id="product-gift-message-count"
                                label={t('gift.messageCharacterCount', {
                                    current: messageLength,
                                    max: PRODUCT_GIFT_MESSAGE_MAX_LENGTH,
                                })}
                            />
                        </div>
                    </div>

                    <SheetFooter className="shrink-0 border-t border-neutral-80 bg-neutral-white px-6 pb-6 pt-4 sm:flex-row">
                        <div className="flex w-full gap-2">
                            <Button
                                type="button"
                                hierarchy="secondary"
                                mode="light"
                                size="large"
                                className="flex-1 cursor-pointer uppercase"
                                onClick={onCancel}>
                                {t('gift.cancel')}
                            </Button>
                            <Button
                                type="submit"
                                hierarchy="primary"
                                mode="light"
                                size="large"
                                className="flex-1 cursor-pointer uppercase">
                                {t('gift.save')}
                            </Button>
                        </div>
                    </SheetFooter>
                </form>
            </SheetContent>
        </Sheet>
    );
}
